import { CSSProperties, useContext, useEffect, useState } from 'react';
import { GOT_TOURNAMENT_ADDON } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import GameContext from 'contexts/GameContext';
import Decimal from 'decimal.js';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';
import { findPlayerTournamentStatus, remainingAddOns } from 'utils/addOnEligibility';
import { clearAddOnInitiator, getAddOnInitiator } from 'utils/addOnInitiator';
import { decompressData } from 'utils/functions';

import GameModal, {
  primaryBtnStyle,
  secondaryBtnStyle,
  btnRowStyle
} from 'components/GameModal/GameModal';

const messageStyle: CSSProperties = {
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.5,
  textAlign: 'center'
};

const AddOnChipsModal = () => {
  const { addOn } = useGameService();
  const { openAddOnChips, setOpenAddOnChips, confirmedAddOns, setConfirmedAddOns } =
    useContext(GameContext);
  const addOnPrice = useTableSelector((table) => table.game.addOnPrice);
  const addOnChips = useTableSelector((table) => table.game.addOnChips);
  const [msgTitle, setMsgTitle] = useState('');
  const [msg, setMsg] = useState('');
  const tournamentStatus = useTableSelector((table) => table.tournamentStatus);
  const game = useTableSelector((table) => table.game);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const remainingAddOn =
    remainingAddOns({
      playerStatus: findPlayerTournamentStatus(tournamentStatus, game.tournamentId, playerGUID),
      tableAddOnLimit: (game as any).addOn,
      confirmedAddOns,
      tournamentId: game.tournamentId
    }) ?? 0;

  const { socket } = useContext(SocketContext);

  const handleAddChips = () => {
    addOn();
    setOpenAddOnChips(false);
    setMsgTitle('');
    setMsg('');
  };

  const cancelAddChips = () => {
    setMsgTitle('');
    setMsg('');
    setOpenAddOnChips(false);
  };

  useEffect(() => {
    if (!socket) return;

    const cellGameID = String(game.gameID ?? '');
    const cellTournamentID = String(game.tournamentId ?? '');
    const handleAddOnEvent = (raw: any) => {
      const data = decompressData(raw);
      if (data) {
        // The shared socket fans this event to EVERY open cell's listener, but
        // the success payload has no gameID. Show it ONLY on the table that
        // initiated the add-on (recorded by GameService.rebuy/addOn). STRICT
        // match — never a "show when no initiator" fallback: the success arrives
        // on all listeners in sequence, and the matching cell clears the
        // initiator, so a fallback would let every cell whose handler runs after
        // it re-open the modal (the exact "shows on all tables" bug).
        const initiator = getAddOnInitiator();
        if (initiator !== cellGameID) return;
        clearAddOnInitiator();
        // The engine reports the player's add-on count on success and on the
        // limit/insufficient-funds refusals. Record it so the Add-On button
        // hides as soon as none are left, without waiting for gotTnmStatus.
        const currentAddons = Number(data?.currentAddons);
        if (cellTournamentID && Number.isFinite(currentAddons)) {
          setConfirmedAddOns({ tournamentId: cellTournamentID, count: currentAddons });
        }
        setOpenAddOnChips(true);
        setMsgTitle(data?.title);
        setMsg(data?.msg);
      } else {
        setMsgTitle('');
        setMsg('');
        setOpenAddOnChips(false);
      }
    };

    socket.on(GOT_TOURNAMENT_ADDON, handleAddOnEvent);
    // Remove THIS cell's handler on unmount/re-run. The previous code put the
    // socket.off inside the event callback's return, so it never ran and
    // listeners accumulated across re-renders.
    return () => {
      socket.off(GOT_TOURNAMENT_ADDON, handleAddOnEvent);
    };
  }, [socket, game.gameID, game.tournamentId, setOpenAddOnChips, setConfirmedAddOns]);

  const modalTitle = msgTitle !== '' ? msgTitle : 'Add-On Chips';

  const defaultMsg = `Please note that proceeding with this action will incur a $${new Decimal(
    addOnPrice === null ? 0 : addOnPrice
  )
    .toDP(2)
    .toNumber()} charge, and you will receive ${addOnChips} chips. You still have ${Math.max(
    0,
    remainingAddOn
  )} more add-on(s) left.`;

  return (
    // Tapping outside dismisses, like every other modal: `cancelAddChips` only
    // clears local state, so a dismiss is a pure cancel — the $ charge lives in
    // `handleAddChips`/`addOn()`, behind the Proceed button.
    <GameModal open={openAddOnChips} onClose={cancelAddChips} title={modalTitle}>
      <div style={messageStyle}>
        {msg !== '' ? msg : defaultMsg}
      </div>

      {/* In the success / result state (msgTitle pushed by server), the only
          action is "dismiss" — and the floating X already handles that. Hide
          the redundant Close button and only render the Cancel/Proceed pair
          for the pre-action prompt. */}
      {msgTitle === '' && (
        <div style={btnRowStyle}>
          <button style={secondaryBtnStyle} onClick={cancelAddChips}>
            Cancel
          </button>
          <button style={primaryBtnStyle} onClick={handleAddChips}>
            Proceed
          </button>
        </div>
      )}
    </GameModal>
  );
};

export default AddOnChipsModal;
